import { useMemo } from 'react';

interface CallProgressDashboardProps {
  totalRequired: number;
  completed: number;
  remaining: number;
  completionPercentage: number;
  billingMonth?: string; // Format: "YYYY-MM" or Date display string
}

type PaceStatus = 'on-track' | 'watch' | 'behind';

export default function CallProgressDashboard({
  totalRequired,
  completed,
  remaining,
  completionPercentage,
  billingMonth,
}: CallProgressDashboardProps) {
  // Calculate pace information
  const paceInfo = useMemo(() => {
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const dayOfMonth = today.getDate();
    const daysRemaining = daysInMonth - dayOfMonth;

    // Expected progress based on day of month
    const expectedProgress = (dayOfMonth / daysInMonth) * totalRequired;
    const actualProgress = completed;

    // Determine pace status
    let paceStatus: PaceStatus;
    if (actualProgress >= expectedProgress * 0.75) {
      paceStatus = 'on-track';
    } else if (actualProgress >= expectedProgress * 0.5) {
      paceStatus = 'watch';
    } else {
      paceStatus = 'behind';
    }

    // Calculate daily rate needed to complete on time
    const dailyRateNeeded = daysRemaining > 0 ? remaining / daysRemaining : remaining;

    return {
      daysRemaining,
      daysInMonth,
      dayOfMonth,
      expectedProgress,
      paceStatus,
      dailyRateNeeded,
    };
  }, [totalRequired, completed, remaining]);

  // Get display month name
  const monthDisplay = useMemo(() => {
    if (billingMonth && billingMonth.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = billingMonth.split('-');
      return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }
    // Default to current month
    return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [billingMonth]);

  // Status colors and messages
  const statusConfig = {
    'on-track': {
      color: 'text-green-700',
      bgColor: 'bg-green-100',
      barColor: 'bg-green-500',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'On track',
    },
    'watch': {
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
      barColor: 'bg-amber-500',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      label: 'Watch pace',
    },
    'behind': {
      color: 'text-red-700',
      bgColor: 'bg-red-100',
      barColor: 'bg-red-500',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: 'Behind pace',
    },
  };

  const status = statusConfig[paceInfo.paceStatus];

  // Don't render if no calls required
  if (totalRequired === 0) {
    return null;
  }

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span className="font-semibold text-slate-800">Call Progress</span>
        </div>
        <span className="text-sm text-slate-500">{monthDisplay}</span>
      </div>

      {/* Progress Bar */}
      <div className="mb-2">
        <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className={`h-full transition-all duration-500 ${status.barColor}`}
            style={{ width: `${Math.min(completionPercentage, 100)}%` }}
          />
        </div>
      </div>

      {/* Progress Numbers */}
      <div className="mb-3 text-sm text-slate-600">
        <span className="font-semibold text-slate-800">{completed}</span> of{' '}
        <span className="font-semibold text-slate-800">{totalRequired}</span> completed (
        {Math.round(completionPercentage)}%)
      </div>

      {/* Pace Status */}
      <div className={`flex items-start gap-2 rounded-lg p-3 ${status.bgColor}`}>
        <span className={status.color}>{status.icon}</span>
        <div className="flex-1">
          <div className={`font-medium ${status.color}`}>
            {status.label}: {remaining} remaining
            {paceInfo.daysRemaining > 0 && (
              <span className="font-normal">
                {' '}
                &middot; {paceInfo.daysRemaining} day{paceInfo.daysRemaining !== 1 ? 's' : ''} left
              </span>
            )}
          </div>
          {remaining > 0 && paceInfo.daysRemaining > 0 && (
            <div className={`mt-0.5 text-sm ${status.color} opacity-80`}>
              Need ~{paceInfo.dailyRateNeeded.toFixed(1)} visits/day to complete on time
            </div>
          )}
          {remaining > 0 && paceInfo.daysRemaining === 0 && (
            <div className={`mt-0.5 text-sm ${status.color} opacity-80`}>
              Last day of month - {remaining} visits still needed
            </div>
          )}
          {remaining === 0 && (
            <div className="mt-0.5 text-sm text-green-700 opacity-80">
              Monthly quota complete!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
